/**
 * SalesOrderStatusPills Component
 * 
 * Displays status pills for Sales Order source and status.
 * 
 * @component
 * @example
 * ```tsx
 * <SalesOrderStatusPills
 *   source="System"
 *   status="Sent"
 * />
 * ```
 */

import { 
  Settings, 
  UserRound, 
  CheckCircle2, 
  PauseCircle, 
  XCircle, 
  Clock, 
  AlertCircle,
  Send,
  StopCircle
} from 'lucide-react';

import { Badge } from '@/components/evo/ui/badge';
import { SalesOrderSourcePascalCaseEnum, SalesOrderStatusEnum } from '@/graphql/contracts/_generated_/contracts';

interface SalesOrderStatusPillsProps {
  source?: SalesOrderSourcePascalCaseEnum | null;
  status?: SalesOrderStatusEnum | null;
}

interface StatusConfig {
  color: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}

type SourceConfigValue = { color: string; icon: React.ComponentType<{ className?: string }> };
const SOURCE_CONFIG: Record<SalesOrderSourcePascalCaseEnum, SourceConfigValue> = {
  System: {
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 border-blue-200 dark:border-blue-800',
    icon: Settings,
  },
  Manual: {
    color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 '
      + 'border-purple-200 dark:border-purple-800',
    icon: UserRound,
  },
};

const STATUS_CONFIG: Record<SalesOrderStatusEnum, StatusConfig> = {
  CREATED: {
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700',
    icon: CheckCircle2,
    label: 'Created',
  },
  SENDING_PAUSED: {
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700',
    icon: CheckCircle2,
    label: 'Sending Paused',
  },
  SENDING_SCHEDULING_FAILED: {
    color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 border-red-200 dark:border-red-800',
    icon: XCircle,
    label: 'Scheduling Failed',
  },
  SENDING_SCHEDULED: {
    color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 border-amber-200 dark:border-amber-800',
    icon: Clock,
    label: 'Sending Scheduled',
  },
  SENDING_FAILED: {
    color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 border-red-200 dark:border-red-800',
    icon: AlertCircle,
    label: 'Sending Failed',
  },
  SENDING_STOPPED: {
    color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 '
      + 'border-orange-200 dark:border-orange-800',
    icon: StopCircle,
    label: 'Sending Stopped',
  },
  SENT: {
    color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 border-green-200 dark:border-green-800',
    icon: Send,
    label: 'Sent',
  },
  BILLING_PAUSED: {
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 '
      + 'border-yellow-200 dark:border-yellow-800',
    icon: PauseCircle,
    label: 'Billing Paused',
  },
};

export function SalesOrderStatusPills({ source, status }: SalesOrderStatusPillsProps) {
  // Don't render if neither source nor status is available
  if (!source && !status) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Source Pill */}
      {source && (
        <Badge variant="outline" className={`${SOURCE_CONFIG[source].color} border font-medium`}>
          {(() => {
            const Icon = SOURCE_CONFIG[source].icon;
            return <Icon className="h-3 w-3 mr-1" />;
          })()}
          {source}
        </Badge>
      )}

      {/* Status Pill */}
      {status && STATUS_CONFIG[status] && (
        <Badge variant="outline" className={`${STATUS_CONFIG[status].color} border font-medium`}>
          {(() => {
            const Icon = STATUS_CONFIG[status].icon;
            return <Icon className="h-3 w-3 mr-1" />;
          })()}
          {STATUS_CONFIG[status].label}
        </Badge>
      )}
    </div>
  );
}
